import * as z from 'zod/mini';
import {
  SHORT_TEXT_MAX_LENGTH,
  VLAN_NAME_MAX_LENGTH,
  VLAN_NOTES_MAX_LENGTH,
  VLAN_TAG_MAX,
  VLAN_TAG_MIN,
} from '../constants.ts';
import { cidrFamily, ipFamily, isCidr, isIpAddress } from '../network.ts';
import { idSchema, sortOrderSchema, timestampSchema } from './common.ts';

// A client's VLAN and subnet table: one row per VLAN, so the tag typed on a
// switch port or a WAN link has a name, a subnet, and a gateway recorded once
// instead of in every device's notes. A row names the client it belongs to
// and, when it is one property's own VLAN rather than one the whole client
// shares, that property (ROADMAP G1). This is documentation, not address
// management: the DHCP range and the reservations beside it (G5) record what
// a router was configured with, and nothing is pushed or scanned.
//
// A tag is unique within a property, and a client-wide tag is unique within
// its client (the database says so); the same tag on another property is
// another VLAN entirely, and a tag cannot be both client-wide and a
// property's own (the route refuses it with a sentence).

const shortText = z.string().check(z.trim(), z.maxLength(SHORT_TEXT_MAX_LENGTH));

/** The 802.1Q tag as a number, in range; the table never holds an untagged row. */
export const vlanTagNumberSchema = z
  .int()
  .check(
    z.gte(VLAN_TAG_MIN, `A VLAN tag is ${String(VLAN_TAG_MIN)}–${String(VLAN_TAG_MAX)}`),
    z.lte(VLAN_TAG_MAX, `A VLAN tag is ${String(VLAN_TAG_MIN)}–${String(VLAN_TAG_MAX)}`),
  );

const vlanNameSchema = z
  .string()
  .check(z.trim(), z.minLength(1, 'Required'), z.maxLength(VLAN_NAME_MAX_LENGTH));

/** The subnet as CIDR text, or blank while nobody has recorded one. */
const vlanSubnetSchema = shortText.check(
  z.refine(
    (text) => text === '' || isCidr(text),
    'A subnet is an address and a prefix, such as 10.0.10.0/24',
  ),
);

/** The gateway's own address, or blank; a prefix belongs on the subnet, not here. */
const vlanGatewaySchema = shortText.check(
  z.refine((text) => text === '' || isIpAddress(text), 'A gateway is an IP address'),
);

/** The IPv6 subnet beside the IPv4 one (ROADMAP G4); blank until one is recorded. */
const vlanSubnet6Schema = shortText.check(
  z.refine(
    (text) => text === '' || cidrFamily(text) === 'ipv6',
    'An IPv6 subnet is an address and a prefix, such as 2001:db8:10::/64',
  ),
);

/** The IPv6 gateway; blank, or an address of that family. */
const vlanGateway6Schema = shortText.check(
  z.refine(
    (text) => text === '' || ipFamily(text) === 'ipv6',
    'An IPv6 gateway is an IPv6 address',
  ),
);

/** One end of the DHCP range the router hands out of this subnet (ROADMAP G5). */
const dhcpEndpointSchema = shortText.check(
  z.refine((text) => text === '' || isIpAddress(text), 'A DHCP range end is an IP address'),
);

const vlanNotesSchema = z.string().check(z.trim(), z.maxLength(VLAN_NOTES_MAX_LENGTH));

export const vlanSchema = z.object({
  id: idSchema,
  clientId: idSchema,
  /** The property whose own VLAN this is, or null for one the client shares. */
  propertyId: z.nullable(idSchema),
  tag: vlanTagNumberSchema,
  name: z.string(),
  subnet: z.string(),
  gateway: z.string(),
  purpose: z.string(),
  notes: z.string(),
  subnet6: z.string(),
  gateway6: z.string(),
  dhcpStart: z.string(),
  dhcpEnd: z.string(),
  sortOrder: sortOrderSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Vlan = z.infer<typeof vlanSchema>;

const vlanFields = {
  tag: vlanTagNumberSchema,
  name: vlanNameSchema,
  subnet: vlanSubnetSchema,
  gateway: vlanGatewaySchema,
  purpose: shortText,
  notes: vlanNotesSchema,
  subnet6: vlanSubnet6Schema,
  gateway6: vlanGateway6Schema,
  dhcpStart: dhcpEndpointSchema,
  dhcpEnd: dhcpEndpointSchema,
};

export const vlanInputSchema = z.object(vlanFields);
export type VlanInput = z.infer<typeof vlanInputSchema>;

/** A patch is a partial of the plain fields, so an edit may move one cell. */
export const vlanPatchSchema = z.partial(z.object(vlanFields));
export type VlanPatch = z.infer<typeof vlanPatchSchema>;

export const vlanEnvelopeSchema = z.object({ vlan: vlanSchema });

export const vlanListSchema = z.object({
  /** The VLANs that apply, client-wide rows first, then by tag. */
  vlans: z.array(vlanSchema),
});

/** Field key → the label its form shows; the history names edits by these. */
export const VLAN_FIELD_LABELS: Readonly<Record<keyof VlanInput, string>> = {
  tag: 'Tag',
  name: 'Name',
  subnet: 'Subnet',
  gateway: 'Gateway',
  purpose: 'Purpose',
  notes: 'Notes',
  subnet6: 'IPv6 subnet',
  gateway6: 'IPv6 gateway',
  dhcpStart: 'DHCP range start',
  dhcpEnd: 'DHCP range end',
};
